import { createHash } from "crypto";
import {
  prisma,
  Prisma,
  RSVPvalues,
  TemplateV2For,
  TemplateTargetEntityType,
  TemplateV2,
  TemplateV2Level,
  TemplateV2Type,
  TriggerV2Type,
} from "client";

export type TargetContext = {
  entityType: TemplateTargetEntityType;
  entityId: string;
};

export type TemplateResolveLevel = Extract<
  TemplateV2Level,
  typeof TemplateV2Level.PLATFORM | typeof TemplateV2Level.WHITE_LABEL
>;

export type ResolveTemplateV2Input = {
  type: TemplateV2Type;
  templateFor: TemplateV2For;
  trigger?: TriggerV2Type | null;
  scopes: {
    eventId?: string | null;
    organizationId?: string | null;
    whiteLabelId?: string | null;
  };
  /**
   * One context entry per order item (ticket or floor plan tier).
   * The resolver promotes the first event-level template whose targets overlap
   * with ANY of the provided contexts, then falls back to an untargeted
   * event-level template, and finally cascades through ORG → WL → PLATFORM.
   */
  targetContexts?: TargetContext[] | null;
  /**
   * Restricts which scope levels are considered when resolving a template.
   * - PLATFORM: platform templates only (e.g. default org on new white label).
   * - WHITE_LABEL: white-label templates first, then platform fallback.
   */
  templateResolveLevel?: TemplateResolveLevel;
};

type LevelScopeFilter = {
  level: TemplateV2Level;
  scopeId: string | null;
};

export const createTemplateV2 = async (
  data: Prisma.TemplateV2UncheckedCreateInput
) => {
  return prisma.templateV2.create({
    data,
  });
};

const TEMPLATE_V2_CLONE_BATCH_SIZE = 500;

export const clonePlatformTemplateV2ToWhiteLabel = async (
  whiteLabelId: string,
  createdById: string
) => {
  let cursorId: string | undefined;

  while (true) {
    const platformTemplates = await prisma.templateV2.findMany({
      where: {
        level: TemplateV2Level.PLATFORM,
        scopeId: null,
        deletedAt: null,
      },
      orderBy: {
        id: "asc",
      },
      take: TEMPLATE_V2_CLONE_BATCH_SIZE,
      ...(cursorId
        ? {
            cursor: { id: cursorId },
            skip: 1,
          }
        : {}),
    });

    if (!platformTemplates.length) {
      break;
    }

    await prisma.templateV2.createMany({
      data: platformTemplates.map((template) => ({
        name: template.name,
        type: template.type,
        templateFor: template.templateFor,
        trigger: template.trigger,
        level: TemplateV2Level.WHITE_LABEL,
        scopeId: whiteLabelId,
        targets: template.targets,
        targetHash: template.targetHash,
        isActive: template.isActive,
        isDefault: template.isDefault,
        html: template.html,
        json: template.json,
        config: template.config,
        locale: template.locale,
        createdById,
        deletedAt: null,
      })),
    });

    cursorId = platformTemplates[platformTemplates.length - 1]?.id;

    if (platformTemplates.length < TEMPLATE_V2_CLONE_BATCH_SIZE) {
      break;
    }
  }
};

export const updateTemplateV2 = async (
  id: string,
  data: Prisma.TemplateV2UncheckedUpdateInput
) => {
  return prisma.templateV2.update({
    where: { id },
    data,
  });
};

export const getTemplateV2ById = async (id: string) => {
  return prisma.templateV2.findUnique({
    where: { id },
  });
};

export const getTemplateV2ByIds = async (ids: string[]) => {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (uniqueIds.length === 0) return [];

  return prisma.templateV2.findMany({
    where: { id: { in: uniqueIds } },
    select: { id: true, json: true },
  });
};

type GetTemplateV2ListInput = {
  level: TemplateV2Level;
  scopeId: string | null;
}[];

export const getTemplateV2List = async (input: GetTemplateV2ListInput) => {
  return prisma.templateV2.findMany({
    where: {
      deletedAt: null,
      OR: input.map((item) => ({
        level: item.level,
        scopeId: item.scopeId,
      })),
    },
    orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
  });
};

const COMMUNICATION_TEMPLATE_FOR_VALUES = (
  Object.values(TemplateV2For) as TemplateV2For[]
).filter((value) => value.endsWith("_COMMUNICATION"));

/**
 * Returns event-scoped design templates (HTML pages and PDF assets) for the
 * design templates admin page.
 *
 * "Design" templates here mean visual/document templates managed in the design
 * templates section — explicitly excluding sending-channel communication
 * templates (EMAIL/SMS/WHATSAPP/NOTIFICATION), which are listed separately.
 *
 * Invitation-template linkage is NOT applied here; that relationship is
 * resolved through TemplateV2 `targets` via `getTemplateV2LinkedToInvitation`
 * and the invitation services.
 */
export const getEventDesignTemplates = async (
  eventId: string
): Promise<TemplateV2[]> => {
  return prisma.templateV2.findMany({
    where: {
      level: TemplateV2Level.EVENT,
      scopeId: eventId,
      deletedAt: null,
      type: { in: [TemplateV2Type.HTML, TemplateV2Type.PDF] },
      templateFor: { notIn: COMMUNICATION_TEMPLATE_FOR_VALUES },
    },
    orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
  });
};

export const softDeleteTemplateV2 = async (id: string) => {
  return prisma.templateV2.update({
    where: { id },
    data: {
      deletedAt: new Date(),
      isActive: false,
    },
  });
};

const getScopeFilters = (
  scopes: ResolveTemplateV2Input["scopes"],
  templateResolveLevel?: TemplateResolveLevel
) => {
  if (templateResolveLevel === TemplateV2Level.PLATFORM) {
    return [
      {
        level: TemplateV2Level.PLATFORM,
        scopeId: null,
      },
    ];
  }

  if (templateResolveLevel === TemplateV2Level.WHITE_LABEL) {
    const filters: LevelScopeFilter[] = [];
    if (scopes.whiteLabelId) {
      filters.push({
        level: TemplateV2Level.WHITE_LABEL,
        scopeId: scopes.whiteLabelId,
      });
    }
    filters.push({
      level: TemplateV2Level.PLATFORM,
      scopeId: null,
    });
    return filters;
  }

  const filters: LevelScopeFilter[] = [];

  // Build fallback order explicitly.
  // Missing scope ids are skipped (e.g. no eventId means no EVENT candidate).
  if (scopes.eventId) {
    filters.push({
      level: TemplateV2Level.EVENT,
      scopeId: scopes.eventId,
    });
  }

  if (scopes.organizationId) {
    filters.push({
      level: TemplateV2Level.ORGANIZATION,
      scopeId: scopes.organizationId,
    });
  }

  if (scopes.whiteLabelId) {
    filters.push({
      level: TemplateV2Level.WHITE_LABEL,
      scopeId: scopes.whiteLabelId,
    });
  }

  // PLATFORM is always last and always present as final fallback.
  filters.push({
    level: TemplateV2Level.PLATFORM,
    scopeId: null,
  });

  return filters;
};

const isTargetedEventTemplate = (template: TemplateV2) => {
  return (
    template.level === TemplateV2Level.EVENT && template.targets.length > 0
  );
};

/**
 * Returns true if the template's target set overlaps with ANY of the provided
 * contexts. A single match is sufficient — this lets one order with mixed item
 * types (e.g. VIP ticket + regular ticket) still hit a targeted template.
 */
const matchesAnyTargetContext = (
  template: TemplateV2,
  targetContexts: ResolveTemplateV2Input["targetContexts"]
): boolean => {
  if (!targetContexts?.length) return false;

  return targetContexts.some((ctx) =>
    template.targets.some(
      (target) =>
        target.entityType === ctx.entityType &&
        target.entityIds.includes(ctx.entityId)
    )
  );
};

export type ResolveTemplatesV2Input = {
  type: TemplateV2Type;
  templateFor: TemplateV2For;
  trigger?: TriggerV2Type | null;
  scopes: ResolveTemplateV2Input["scopes"];
  templateResolveLevel?: TemplateResolveLevel;
  /**
   * When provided, event-level templates are filtered to those whose targets
   * contain at least one of the given entityIds for the given entityType.
   * Untargeted event-level templates (targets = []) are always included.
   */
  targets?: {
    entityType: TemplateTargetEntityType;
    entityIds: string[];
  } | null;
};

/**
 * Like resolveTemplateV2 but returns every matching template across all scope
 * levels instead of stopping at the first winner.
 *
 * At the EVENT level:
 *   - Templates whose targets include the given entityType + any of the given
 *     entityIds are included.
 *   - Untargeted event-level templates (targets = []) are always included.
 * At ORG / WL / PLATFORM levels all active candidates are included.
 *
 * The returned array is ordered by scope priority
 * (EVENT → ORG → WL → PLATFORM) and then by updatedAt/createdAt desc within
 * each level.
 */
export const resolveTemplatesV2 = async (
  input: ResolveTemplatesV2Input
): Promise<TemplateV2[]> => {
  const scopeFilters = getScopeFilters(
    input.scopes,
    input.templateResolveLevel
  );

  const candidates = await prisma.templateV2.findMany({
    where: {
      type: input.type,
      trigger: input.trigger ?? null,
      isActive: true,
      deletedAt: null,
      OR: scopeFilters.map((filter) => ({
        level: filter.level,
        scopeId: filter.scopeId,
      })),
    },
    orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
  });

  const results: TemplateV2[] = [];

  for (const filter of scopeFilters) {
    const levelCandidates = candidates.filter(
      (candidate) =>
        candidate.level === filter.level && candidate.scopeId === filter.scopeId
    );

    if (!levelCandidates.length) continue;

    if (filter.level === TemplateV2Level.EVENT) {
      if (input.targets?.entityIds.length) {
        const { entityType, entityIds } = input.targets;

        // Include targeted templates that match the given entityType + entityIds.
        const targetedMatches = levelCandidates.filter((candidate) =>
          candidate.targets.some(
            (t) =>
              t.entityType === entityType &&
              t.entityIds.some((id) => entityIds.includes(id))
          )
        );
        results.push(...targetedMatches);
      }

      // Always include untargeted event-level templates (targets = []).
      const untargetedMatches = levelCandidates.filter(
        (candidate) => candidate.targets.length === 0
      );
      results.push(...untargetedMatches);
    } else {
      results.push(...levelCandidates);
    }
  }

  return results;
};

export const resolveTemplateV2 = async (
  input: ResolveTemplateV2Input
): Promise<TemplateV2 | null> => {
  const scopeFilters = getScopeFilters(
    input.scopes,
    input.templateResolveLevel
  );

  const candidates = await prisma.templateV2.findMany({
    where: {
      type: input.type,
      templateFor: input.templateFor,
      trigger: input.trigger ?? null,
      isActive: true,
      deletedAt: null,
      // This OR is a set filter, not a priority mechanism.
      // It fetches all candidates that match ANY allowed scope pair.
      // Priority is applied later in-memory using scopeFilters order.
      OR: scopeFilters.map((filter) => ({
        level: filter.level,
        scopeId: filter.scopeId,
      })),
    },
    orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
  });

  // Deterministic fallback: EVENT -> ORG -> WL -> PLATFORM (or whichever exists).
  // If eventId/organizationId/whiteLabelId is missing, that level is not evaluated.
  for (const filter of scopeFilters) {
    const levelCandidates = candidates.filter(
      (candidate) =>
        candidate.level === filter.level && candidate.scopeId === filter.scopeId
    );

    if (!levelCandidates.length) continue;

    if (filter.level === TemplateV2Level.EVENT) {
      // On event level, prefer explicit target match when targetContexts exist.
      if (input.targetContexts?.length) {
        const targetedMatch = levelCandidates.find(
          (candidate) =>
            isTargetedEventTemplate(candidate) &&
            matchesAnyTargetContext(candidate, input.targetContexts)
        );

        if (targetedMatch) return targetedMatch;
      }

      // Fallback to generic event-level template (no targets).
      const untargetedMatch = levelCandidates.find(
        (candidate) => !isTargetedEventTemplate(candidate)
      );

      if (untargetedMatch) return untargetedMatch;
      continue;
    }

    return levelCandidates[0] ?? null;
  }

  return null;
};

type TemplateV2TargetInput = {
  entityType: TemplateTargetEntityType;
  entityIds: string[];
};

const normalizeTemplateV2Targets = (targets: TemplateV2TargetInput[] = []) => {
  const groupedByEntityType = new Map<string, Set<string>>();

  for (const target of targets) {
    const bucket =
      groupedByEntityType.get(target.entityType) ?? new Set<string>();
    for (const entityId of target.entityIds) {
      bucket.add(entityId);
    }
    groupedByEntityType.set(target.entityType, bucket);
  }

  return Array.from(groupedByEntityType.entries())
    .map(([entityType, entityIds]) => ({
      entityType: entityType as TemplateTargetEntityType,
      entityIds: Array.from(entityIds).sort(),
    }))
    .sort((a, b) => a.entityType.localeCompare(b.entityType));
};

export const buildTemplateV2TargetHash = (
  targets: TemplateV2TargetInput[] = []
) => {
  return createHash("sha256")
    .update(JSON.stringify(normalizeTemplateV2Targets(targets)))
    .digest("hex");
};

const templateV2TargetsInvitation = (
  targets: TemplateV2TargetInput[],
  invitationId: string
) =>
  targets.some(
    (target) =>
      target.entityType === TemplateTargetEntityType.INVITATION &&
      target.entityIds.includes(invitationId)
  );

const addInvitationToTemplateV2Targets = (
  targets: TemplateV2TargetInput[],
  invitationId: string
): TemplateV2TargetInput[] => {
  const invitationTarget = targets.find(
    (target) => target.entityType === TemplateTargetEntityType.INVITATION
  );

  if (!invitationTarget) {
    return [
      ...targets,
      {
        entityType: TemplateTargetEntityType.INVITATION,
        entityIds: [invitationId],
      },
    ];
  }

  if (invitationTarget.entityIds.includes(invitationId)) {
    return targets;
  }

  return targets.map((target) =>
    target.entityType === TemplateTargetEntityType.INVITATION
      ? {
          ...target,
          entityIds: [...target.entityIds, invitationId],
        }
      : target
  );
};

const removeInvitationFromTemplateV2Targets = (
  targets: TemplateV2TargetInput[],
  invitationId: string
): TemplateV2TargetInput[] => {
  return normalizeTemplateV2Targets(
    targets
      .map((target) =>
        target.entityType === TemplateTargetEntityType.INVITATION
          ? {
              ...target,
              entityIds: target.entityIds.filter((id) => id !== invitationId),
            }
          : target
      )
      .filter((target) => target.entityIds.length > 0)
  );
};

export const getTemplateV2LinkedToInvitation = async (
  eventId: string,
  invitationId: string
) => {
  const eventTemplates = await prisma.templateV2.findMany({
    where: {
      level: TemplateV2Level.EVENT,
      scopeId: eventId,
      deletedAt: null,
    },
    orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
  });

  return eventTemplates.filter((template) =>
    templateV2TargetsInvitation(template.targets, invitationId)
  );
};

const INVITATION_COMMUNICATION_TEMPLATE_TYPES = new Set<TemplateV2Type>([
  TemplateV2Type.EMAIL,
  TemplateV2Type.SMS,
  TemplateV2Type.WHATSAPP,
]);

const INVITATION_PDF_TEMPLATE_FOR = new Set<TemplateV2For>([
  TemplateV2For.PRINTABLE_INVITATION_PDF,
  TemplateV2For.DIGITAL_INVITATION_PDF,
]);

const isInvitationSendingChannelTemplate = (template: {
  type: TemplateV2Type;
  templateFor: TemplateV2For;
}) => {
  return (
    (INVITATION_COMMUNICATION_TEMPLATE_TYPES.has(template.type) &&
      template.templateFor === TemplateV2For.INVITATION_COMMUNICATION) ||
    (template.type === TemplateV2Type.PDF &&
      INVITATION_PDF_TEMPLATE_FOR.has(template.templateFor))
  );
};

export const mapTemplateV2ToInvitationTemplateShape = (
  templates: TemplateV2[]
) => {
  const notificationTemplates = templates
    .filter((template) =>
      INVITATION_COMMUNICATION_TEMPLATE_TYPES.has(template.type)
    )
    .map((template) => ({
      id: template.id,
      name: template.name,
      type:
        template.type === TemplateV2Type.EMAIL ? "EMAIL_DESIGN" : template.type,
    }));

  const designs = templates
    .filter(
      (template) =>
        template.type === TemplateV2Type.PDF &&
        INVITATION_PDF_TEMPLATE_FOR.has(template.templateFor)
    )
    .map((template) => ({
      id: template.id,
      designId: template.id,
      name: template.name,
    }));

  return { notificationTemplates, designs };
};

export const mapTemplateV2ToEnabledTemplates = (templates: TemplateV2[]) =>
  templates.map((template) => ({
    id: template.id,
    name: template.name,
    type: template.type,
    templateFor: template.templateFor,
    isEmail: template.type === TemplateV2Type.EMAIL,
    isPhysical:
      template.type === TemplateV2Type.PDF &&
      INVITATION_PDF_TEMPLATE_FOR.has(template.templateFor),
  }));

export const RSVP_VALUE_TO_RESPONSE_TEMPLATE_FOR: Record<
  RSVPvalues,
  TemplateV2For
> = {
  [RSVPvalues.YES]: TemplateV2For.INVITATION_RESPONSE_PAGE_YES_HTML,
  [RSVPvalues.NO]: TemplateV2For.INVITATION_RESPONSE_PAGE_NO_HTML,
  [RSVPvalues.MAYBE]: TemplateV2For.INVITATION_RESPONSE_PAGE_MAYBE_HTML,
};

const RSVP_RESPONSE_HTML_TYPE_BY_VALUE: Record<RSVPvalues, string> = {
  [RSVPvalues.YES]: "YES_INVITATION_RESPONSE",
  [RSVPvalues.NO]: "NO_INVITATION_RESPONSE",
  [RSVPvalues.MAYBE]: "MAYBE_INVITATION_RESPONSE",
};

const RSVP_RESPONSE_TEMPLATE_FOR_OPTIONS = Object.values(
  RSVP_VALUE_TO_RESPONSE_TEMPLATE_FOR
);

export const getRSVPTemplateV2IdForInvitation = async (
  eventId: string,
  invitationId: string,
  rsvpValue: RSVPvalues
) => {
  const templateFor = RSVP_VALUE_TO_RESPONSE_TEMPLATE_FOR[rsvpValue];
  const templates = await prisma.templateV2.findMany({
    where: {
      level: TemplateV2Level.EVENT,
      scopeId: eventId,
      type: TemplateV2Type.HTML,
      templateFor,
      deletedAt: null,
    },
    orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
  });

  return (
    templates.find((template) =>
      templateV2TargetsInvitation(template.targets, invitationId)
    )?.id ?? null
  );
};

export const getInvitationsRSVPTemplatesByEventId = async (eventId: string) => {
  const templates = await prisma.templateV2.findMany({
    where: {
      level: TemplateV2Level.EVENT,
      scopeId: eventId,
      type: TemplateV2Type.HTML,
      templateFor: { in: RSVP_RESPONSE_TEMPLATE_FOR_OPTIONS },
      deletedAt: null,
    },
    orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
  });

  return (Object.keys(RSVP_VALUE_TO_RESPONSE_TEMPLATE_FOR) as RSVPvalues[]).map(
    (rsvpValue) => {
      const templateFor = RSVP_VALUE_TO_RESPONSE_TEMPLATE_FOR[rsvpValue];

      return {
        HTMLType: RSVP_RESPONSE_HTML_TYPE_BY_VALUE[rsvpValue],
        templates: templates
          .filter((template) => template.templateFor === templateFor)
          .map((template) => ({
            id: template.id,
            name: template.name,
          })),
      };
    }
  );
};

export const mapTemplateV2ToRSVPThankYouPageShape = (template: TemplateV2) => ({
  id: template.id,
  translations: [{ html: template.html ?? "" }],
});

export const syncRSVPTemplateV2ForInvitation = async (
  tx: Prisma.TransactionClient,
  params: {
    eventId: string;
    invitationId: string;
    rsvpValue: RSVPvalues;
    templateId?: string | null;
  }
) => {
  const templateFor = RSVP_VALUE_TO_RESPONSE_TEMPLATE_FOR[params.rsvpValue];
  const scopedTemplates = await tx.templateV2.findMany({
    where: {
      level: TemplateV2Level.EVENT,
      scopeId: params.eventId,
      type: TemplateV2Type.HTML,
      templateFor,
      deletedAt: null,
    },
  });

  const templatesToUnlink = scopedTemplates.filter((template) =>
    templateV2TargetsInvitation(template.targets, params.invitationId)
  );

  const selectedTemplate = params.templateId
    ? scopedTemplates.find((template) => template.id === params.templateId)
    : null;

  if (params.templateId && !selectedTemplate) {
    throw new Error("RSVP template not found for this event");
  }

  const templatesToLink =
    selectedTemplate &&
    !templateV2TargetsInvitation(selectedTemplate.targets, params.invitationId)
      ? [selectedTemplate]
      : [];

  await Promise.all([
    ...templatesToUnlink.map((template) => {
      const targets = removeInvitationFromTemplateV2Targets(
        template.targets,
        params.invitationId
      );

      return tx.templateV2.update({
        where: { id: template.id },
        data: {
          targets,
          targetHash: buildTemplateV2TargetHash(targets),
        },
      });
    }),
    ...templatesToLink.map((template) => {
      const targets = addInvitationToTemplateV2Targets(
        template.targets,
        params.invitationId
      );

      return tx.templateV2.update({
        where: { id: template.id },
        data: {
          targets,
          targetHash: buildTemplateV2TargetHash(targets),
        },
      });
    }),
  ]);
};

export const saveRSVPTemplateV2 = async (
  templateId: string,
  invitationId: string,
  rsvpValue: RSVPvalues
) => {
  const invitation = await prisma.invitation.findUnique({
    where: { id: invitationId },
    select: { eventId: true },
  });

  if (!invitation) {
    throw new Error("Invitation not found");
  }

  await prisma.$transaction(async (tx) => {
    await syncRSVPTemplateV2ForInvitation(tx, {
      eventId: invitation.eventId,
      invitationId,
      rsvpValue,
      templateId,
    });

    await tx.rSVP.update({
      where: {
        invitationId_value: {
          invitationId,
          value: rsvpValue,
        },
      },
      data: {
        ...(templateId
          ? { thankYouPageTemplate: { connect: { id: templateId } } }
          : { thankYouPageTemplate: { disconnect: true } }),
      },
    });
  });
};

export const unlinkRSVPTemplatesV2ForInvitation = async (
  eventId: string,
  invitationId: string,
  rsvpValue: RSVPvalues
) => {
  await prisma.$transaction(async (tx) => {
    await syncRSVPTemplateV2ForInvitation(tx, {
      eventId,
      invitationId,
      rsvpValue,
      templateId: null,
    });
  });
};

export const enrichRSVPsWithTemplateV2 = async <
  T extends { value: RSVPvalues; thankYouPageTemplateId?: string | null }
>(
  eventId: string,
  invitationId: string,
  rsvps: T[]
) => {
  return Promise.all(
    rsvps.map(async (rsvp) => ({
      ...rsvp,
      thankYouPageTemplateId:
        (await getRSVPTemplateV2IdForInvitation(
          eventId,
          invitationId,
          rsvp.value
        )) ?? rsvp.thankYouPageTemplateId,
    }))
  );
};

export const enrichInvitationWithTemplateV2 = async <
  T extends { id: string; eventId: string; rsvps?: { value: RSVPvalues }[] }
>(
  invitation: T
) => {
  const linkedTemplates = await getTemplateV2LinkedToInvitation(
    invitation.eventId,
    invitation.id
  );

  const enrichedRsvps = invitation.rsvps?.length
    ? await enrichRSVPsWithTemplateV2(
        invitation.eventId,
        invitation.id,
        invitation.rsvps
      )
    : invitation.rsvps;

  return {
    ...invitation,
    ...(enrichedRsvps ? { rsvps: enrichedRsvps } : {}),
    ...mapTemplateV2ToInvitationTemplateShape(linkedTemplates),
    enabledTemplates: mapTemplateV2ToEnabledTemplates(linkedTemplates),
  };
};

export const syncInvitationTemplateV2Targets = async (
  tx: Prisma.TransactionClient,
  params: {
    eventId: string;
    invitationId: string;
    templateIds: string[];
  }
) => {
  const uniqueTemplateIds = [...new Set(params.templateIds.filter(Boolean))];

  const [eventTemplates, selectedTemplates] = await Promise.all([
    tx.templateV2.findMany({
      where: {
        level: TemplateV2Level.EVENT,
        scopeId: params.eventId,
        deletedAt: null,
        OR: [
          {
            templateFor: TemplateV2For.INVITATION_COMMUNICATION,
            type: {
              in: [
                TemplateV2Type.EMAIL,
                TemplateV2Type.SMS,
                TemplateV2Type.WHATSAPP,
              ],
            },
          },
          {
            type: TemplateV2Type.PDF,
            templateFor: { in: [...INVITATION_PDF_TEMPLATE_FOR] },
          },
        ],
      },
    }),
    uniqueTemplateIds.length
      ? tx.templateV2.findMany({
          where: {
            id: { in: uniqueTemplateIds },
            level: TemplateV2Level.EVENT,
            scopeId: params.eventId,
            deletedAt: null,
          },
        })
      : Promise.resolve([]),
  ]);

  const sendingChannelEventTemplates = eventTemplates.filter(
    isInvitationSendingChannelTemplate
  );
  const sendingChannelSelectedTemplates = selectedTemplates.filter(
    isInvitationSendingChannelTemplate
  );

  const selectedTemplateIdSet = new Set(
    sendingChannelSelectedTemplates.map((template) => template.id)
  );

  const templatesToUnlink = sendingChannelEventTemplates.filter(
    (template) =>
      templateV2TargetsInvitation(template.targets, params.invitationId) &&
      !selectedTemplateIdSet.has(template.id)
  );

  const templatesToLink = sendingChannelSelectedTemplates.filter(
    (template) =>
      !templateV2TargetsInvitation(template.targets, params.invitationId)
  );

  await Promise.all([
    ...templatesToUnlink.map((template) => {
      const targets = removeInvitationFromTemplateV2Targets(
        template.targets,
        params.invitationId
      );

      return tx.templateV2.update({
        where: { id: template.id },
        data: {
          targets,
          targetHash: buildTemplateV2TargetHash(targets),
        },
      });
    }),
    ...templatesToLink.map((template) => {
      const targets = addInvitationToTemplateV2Targets(
        template.targets,
        params.invitationId
      );

      return tx.templateV2.update({
        where: { id: template.id },
        data: {
          targets,
          targetHash: buildTemplateV2TargetHash(targets),
        },
      });
    }),
  ]);
};
